import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getTransactions, getTransactionStats } from '../utils/api';
import {
  DollarSign,
  CreditCard,
  Clock,
  BarChart3,
  Wallet,
  ShieldCheck
} from 'lucide-react';
import '../styles/PaymentsPage.css';

const PaymentsPage = () => {
  const [activeTab, setActiveTab] = useState('all');

  const { data: transactions, isLoading: transLoading } = useApi(getTransactions, { immediate: true });
  const { data: stats } = useApi(getTransactionStats, { immediate: true });

  const statusBadge = (status) => {
    const styles = {
      completed: 'bg-green-500/20 text-green-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      failed: 'bg-red-500/20 text-red-400',
      refunded: 'bg-purple-500/20 text-purple-400',
      processing: 'bg-blue-500/20 text-blue-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status === 'completed' ? 'Concluído' :
         status === 'pending' ? 'Pendente' :
         status === 'failed' ? 'Falhou' :
         status === 'refunded' ? 'Reembolsado' :
         status === 'processing' ? 'Processando' : status}
      </span>
    );
  };

  const tabs = [
    { id: 'all', label: 'Todas', count: (transactions || []).length },
    { id: 'completed', label: 'Concluídas', count: (transactions || []).filter(t => t.status === 'completed').length },
    { id: 'pending', label: 'Pendentes', count: (transactions || []).filter(t => t.status === 'pending').length },
    { id: 'failed', label: 'Falhas', count: (transactions || []).filter(t => t.status === 'failed').length },
  ];

  const columns = [
    { key: 'id', label: 'ID', width: '60px' },
    { key: 'request_id', label: 'Pedido', width: '90px' },
    { key: 'client_name', label: 'Cliente' },
    { key: 'worker_name', label: 'Profissional' },
    { key: 'amount', label: 'Valor', render: (row) => (
      <span className="text-green-400 font-medium">Kz {Number(row.amount || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}</span>
    )},
    { key: 'fee', label: 'Taxa prestador', render: (row) => (
      <span className="text-gray-400">Kz {Number(row.fee || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}</span>
    )},
    { key: 'client_fee', label: 'Taxa cliente', render: (row) => (
      <span className="text-cyan-400">Kz {Number(row.client_fee || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}</span>
    )},
    { key: 'client_fee_vat', label: 'IVA cliente', render: (row) => (
      <span className="text-amber-300">Kz {Number(row.client_fee_vat || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}</span>
    )},
    { key: 'provider_fee_vat', label: 'IVA prestador', render: (row) => (
      <span className="text-amber-300">Kz {Number(row.provider_fee_vat || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}</span>
    )},
    { key: 'method', label: 'Método', render: (row) => (
      <div className="flex items-center gap-1">
        <CreditCard className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-sm">{row.method || 'Cartão'}</span>
      </div>
    )},
    { key: 'status', label: 'Status', render: (row) => statusBadge(row.status) },
    { key: 'created_at', label: 'Data', render: (row) =>
      row.created_at ? new Date(row.created_at).toLocaleDateString('pt-PT') : '-'
    },
  ];

  const getTabData = () => {
    if (activeTab === 'all') return transactions || [];
    return (transactions || []).filter(t => t.status === activeTab);
  };

  // Números reais da API (snake_case). Nada de valores de exemplo: se ainda
  // não houver resposta mostra-se "—".
  const num = (v) => (v === null || v === undefined ? null : Number(v));
  const kz  = (v) => (v === null ? '—' : `Kz ${v.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}`);
  const real = {
    totalRevenue:      num(stats?.total_revenue),
    totalTransactions: num(stats?.total_payments),
    avgTransaction:    num(stats?.avg_payment),
    pendingAmount:     num(stats?.pending_revenue),
    pendingCount:      num(stats?.pending_payments),
    commission:        num(stats?.total_commission),
    clientFees:        num(stats?.total_client_fees),
    providerFees:      num(stats?.total_provider_fees),
    feeVat:            num(stats?.total_fee_vat),
    netToWorkers:      num(stats?.total_net_to_workers),
  };

  return (
    <div className="payments-admin-page space-y-6">
      {/* Header */}
      <div className="payments-admin-hero"><div><span>Controlo financeiro</span><h1>Pagamentos e garantia</h1><p>Acompanhe valores movimentados, comissões e fundos protegidos.</p></div><div className="payments-admin-net"><Wallet/><span><small>Libertado aos profissionais</small><strong>{kz(real.netToWorkers)}</strong></span></div></div>

      {/* Revenue Stats */}
      <div className="payments-admin-stats grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="payment-stat payment-stat--green bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-5 border border-green-500/20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-400 text-sm">Receita Dozy acumulada</p>
            <DollarSign className="w-5 h-5 text-green-400" />
          </div>
          <p className="text-2xl font-bold text-white">{kz(real.commission)}</p>
          <p className="text-gray-500 text-xs mt-2">
            Desde o início · cliente {kz(real.clientFees)} · prestador {kz(real.providerFees)} · IVA {kz(real.feeVat)}
          </p>
        </div>

        <div className="payment-stat payment-stat--blue bg-gray-800 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-400 text-sm">Total Transações</p>
            <BarChart3 className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-white">{real.totalTransactions === null ? '—' : real.totalTransactions}</p>
          <p className="text-gray-500 text-xs mt-2">Libertações registadas</p>
        </div>

        <div className="payment-stat payment-stat--purple bg-gray-800 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-400 text-sm">Ticket Médio</p>
            <CreditCard className="w-5 h-5 text-purple-400" />
          </div>
          <p className="text-2xl font-bold text-white">{kz(real.avgTransaction)}</p>
          <p className="text-gray-500 text-xs mt-2">Por libertação</p>
        </div>

        <div className="payment-stat payment-stat--gold bg-gray-800 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-400 text-sm">Pendentes</p>
            <Clock className="w-5 h-5 text-yellow-400" />
          </div>
          <p className="text-2xl font-bold text-white">{kz(real.pendingAmount)}</p>
          <p className="text-gray-500 text-xs mt-2">
            {real.pendingCount ? `Protegido · ${real.pendingCount} pedido(s)` : 'Nada protegido'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="payments-admin-assurance"><ShieldCheck/><span><strong>Fluxo protegido</strong><small>Os valores pendentes permanecem retidos até confirmação ou resolução administrativa.</small></span></div>

      <div className="payments-admin-tabs border-b border-gray-700">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors relative
                ${activeTab === tab.id
                  ? 'text-teal-400 border-b-2 border-teal-400'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
            >
              {tab.label}
              <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs
                ${activeTab === tab.id ? 'bg-teal-500/20 text-teal-400' : 'bg-gray-700 text-gray-400'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {transLoading && !transactions ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <DataTable
          data={getTabData()}
          columns={columns}
          pageSize={10}
          emptyMessage="Nenhuma transação encontrada."
        />
      )}
    </div>
  );
};

export default PaymentsPage;
